import { isNil } from "lodash";

import {
  LIVE_MAP_DEBUG_DRIVER_NAMES,
  LIVE_MAP_DEBUG_PASSENGERS_COUNT_MAX,
  MILLISECONDS_IN_SECOND,
  SECONDS_IN_MINUTE,
} from "../../constants";
import { IRideDto, IRideMetadata } from "../../dto";
import {
  ICarTrackingInfo,
  IRideRoute,
  ITrackingInfoDto,
  ITrackingRenderState,
} from "../../models";
import { dateToComparableNumber } from "../date.helpers";
import {
  buildTrackingRenderStateFromDtos,
  getRideCourseCode,
  getRideCourseLineColorFromDto,
} from "../live-map";
import { trackingDtosToLocations } from "../maps.helpers";
import {
  calculatePassengersInfoSeatsCount,
  getRideRouteInfo,
} from "./rides.helpers";

/** Delay minutes for map tooltip — backend `timeLate` is authoritative when actual exists. */
export const resolveLiveMapTooltipActualArrivalTime = ({
  debugMode,
  actualArrivalTime,
  plannedArrivalTime,
  trackingLastUpdate,
}: {
  debugMode?: boolean;
  actualArrivalTime?: TDateISO;
  plannedArrivalTime?: TDateISO;
  trackingLastUpdate?: TDateISO;
}): TDateISO | undefined => {
  if (actualArrivalTime) {
    return actualArrivalTime;
  }

  if (!debugMode) {
    return undefined;
  }

  return trackingLastUpdate ?? plannedArrivalTime;
};

/** Synthetic driver name for debug-mode vehicle popup preview (no driver-app / manual assignment on the ride). */
export const resolveLiveMapTooltipDriverName = ({
  debugMode,
  driverName,
  rideId,
}: {
  debugMode?: boolean;
  driverName?: string;
  rideId?: number;
}): string | undefined => {
  if (driverName) {
    return driverName;
  }

  if (!debugMode || rideId === undefined) {
    return undefined;
  }

  return LIVE_MAP_DEBUG_DRIVER_NAMES[
    rideId % LIVE_MAP_DEBUG_DRIVER_NAMES.length
  ];
};

const JOURNEY_DATE_SUFFIX = / \d{4}-\d{2}-\d{2}$/;

/** Journey name for the map popup's "Schedule Number" row, with the trailing ETL date stripped when present. */
export const resolveLiveMapTooltipScheduleNumber = (
  journeyName?: string | null,
): string | undefined => {
  if (!journeyName) {
    return undefined;
  }

  return journeyName.replace(JOURNEY_DATE_SUFFIX, "");
};

/** Synthetic passengers count for debug-mode vehicle popup preview (deterministic per ride id, not truly random). */
export const resolveLiveMapTooltipPassengersCount = ({
  debugMode,
  passengersCount,
  rideId,
}: {
  debugMode?: boolean;
  passengersCount?: number;
  rideId?: number;
}): number | undefined => {
  // Outside debug mode, always show the real count as-is — including a genuine 0.
  if (!debugMode) {
    return passengersCount;
  }

  // In debug mode, a real non-zero count still wins; 0 isn't a useful preview, so synthesize instead.
  const hasUsefulRealCount = !isNil(passengersCount) && passengersCount !== 0;

  if (hasUsefulRealCount || rideId === undefined) {
    return passengersCount;
  }

  return (rideId % LIVE_MAP_DEBUG_PASSENGERS_COUNT_MAX) + 1;
};

export type TrackingPopoverDelayKind = "none" | "late" | "early" | "on_time";

export interface ITrackingPopoverDelay {
  kind: TrackingPopoverDelayKind;
  minutes?: number;
}

/** Ignore multi-day planned vs debug-GPS mismatches in popover pair math. */
const LIVE_MAP_POPOVER_MAX_PLANNED_ACTUAL_PAIR_MINUTES = 12 * 60;

export const isPopoverPlannedActualDelayCoherent = ({
  plannedArrivalTime,
  actualArrivalTime,
  maxAbsMinutes = LIVE_MAP_POPOVER_MAX_PLANNED_ACTUAL_PAIR_MINUTES,
}: {
  plannedArrivalTime?: TDateISO;
  actualArrivalTime?: TDateISO;
  maxAbsMinutes?: number;
}): boolean => {
  if (!plannedArrivalTime || !actualArrivalTime) {
    return false;
  }

  const plannedMs = dateToComparableNumber(plannedArrivalTime);
  const actualMs = dateToComparableNumber(actualArrivalTime);

  if (!plannedMs || !actualMs) {
    return false;
  }

  const absDiffMinutes = Math.abs(
    Math.round(
      (actualMs - plannedMs) / (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE),
    ),
  );

  return absDiffMinutes <= maxAbsMinutes;
};

/** Popover delay bucket for map vehicle tooltip (late / early / on-time). */
export const resolveTrackingPopoverDelay = ({
  plannedArrivalTime,
  actualArrivalTime,
  timeLate,
  requireCoherentPlannedActualPair = false,
}: {
  plannedArrivalTime?: TDateISO;
  actualArrivalTime?: TDateISO;
  timeLate?: number;
  /** Debug synthetic actual: allow pair math only when planned/actual are same ride window. */
  requireCoherentPlannedActualPair?: boolean;
}): ITrackingPopoverDelay => {
  if (!actualArrivalTime) {
    const delay: ITrackingPopoverDelay = { kind: "none" };
    return delay;
  }

  if (timeLate !== undefined && timeLate !== null) {
    if (timeLate > 0) {
      const delay: ITrackingPopoverDelay = { kind: "late", minutes: timeLate };
      return delay;
    }

    if (timeLate < 0) {
      const delay: ITrackingPopoverDelay = {
        kind: "early",
        minutes: Math.abs(Math.round(timeLate)),
      };
      return delay;
    }

    const delay: ITrackingPopoverDelay = { kind: "on_time" };
    return delay;
  }

  if (!plannedArrivalTime) {
    const delay: ITrackingPopoverDelay = { kind: "none" };
    return delay;
  }

  if (
    requireCoherentPlannedActualPair &&
    !isPopoverPlannedActualDelayCoherent({
      plannedArrivalTime,
      actualArrivalTime,
    })
  ) {
    const delay: ITrackingPopoverDelay = { kind: "none" };
    return delay;
  }

  const plannedMs = dateToComparableNumber(plannedArrivalTime);
  const actualMs = dateToComparableNumber(actualArrivalTime);

  if (!plannedMs || !actualMs) {
    const delay: ITrackingPopoverDelay = { kind: "none" };
    return delay;
  }

  const delayMinutes = Math.round(
    (actualMs - plannedMs) / (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE),
  );

  if (delayMinutes > 0) {
    const delay: ITrackingPopoverDelay = {
      kind: "late",
      minutes: delayMinutes,
    };
    return delay;
  }

  if (delayMinutes < 0) {
    const delay: ITrackingPopoverDelay = {
      kind: "early",
      minutes: Math.abs(delayMinutes),
    };
    return delay;
  }

  const delay: ITrackingPopoverDelay = { kind: "on_time" };
  return delay;
};

const resolveRideTrackingPassengersCount = (
  metadata?: IRideMetadata,
): number | undefined => {
  if (!metadata) {
    return undefined;
  }

  if (
    metadata.seatsCount !== undefined &&
    metadata.seatsCount !== null &&
    metadata.seatsCount > 0
  ) {
    return metadata.seatsCount;
  }

  const seatsFromPassengersInfo = metadata.passengersInfo?.length
    ? calculatePassengersInfoSeatsCount(metadata.passengersInfo)
    : undefined;

  if (seatsFromPassengersInfo !== undefined && seatsFromPassengersInfo > 0) {
    return seatsFromPassengersInfo;
  }

  if (
    metadata.passengersCount !== undefined &&
    metadata.passengersCount !== null
  ) {
    return metadata.passengersCount;
  }

  if (metadata.seatsCount !== undefined && metadata.seatsCount !== null) {
    return metadata.seatsCount;
  }

  return seatsFromPassengersInfo;
};

const getRideCarInfo = ({
  rideDto,
  trackingDtos,
}: {
  rideDto: IRideDto;
  trackingDtos?: ITrackingInfoDto[];
}): ICarTrackingInfo => {
  const lastTrackingPoint = trackingDtos?.length
    ? trackingDtos[trackingDtos.length - 1]?.insDate
    : undefined;

  const carInfo: ICarTrackingInfo = {
    rideId: rideDto.id || 0,
    metadata: rideDto.metadata,
    supplierName: rideDto.supplierName,
    driverName: rideDto.driverName,
    driverPhone: rideDto.driverPhone,
    externalDriverName: rideDto.externalMetadata?.driver?.name,
    externalDriverPhone: rideDto.externalMetadata?.driver?.phone,
    vehicleName: rideDto.vehicleName,
    vehicleTypeName: rideDto.vehicleTypeName,
    destinationStationName: rideDto.destinationStationName,
    originStationName: rideDto.originStationName,
    rideTypeName: rideDto.rideTypeName,
    plannedArrivalTime: rideDto.plannedArrivalTime,
    actualArrivalTime: rideDto.actualArrivalTime,
    timeLate: rideDto.timeLate,
    statusLate: rideDto.statusLate,
    courseId: rideDto.courseId,
    courseName: rideDto.courseName,
    courseCode: getRideCourseCode(rideDto),
    courseRouteType: rideDto.courseRouteType,
    courseLineColor: getRideCourseLineColorFromDto(rideDto),
    scheduleNumber: resolveLiveMapTooltipScheduleNumber(rideDto.journeyName),
    passengersCount: resolveRideTrackingPassengersCount(rideDto.metadata),
    trackingLastUpdate: lastTrackingPoint,
    siteName: rideDto.siteName,
  };
  return carInfo;
};

/** Builds a live tracking route from a precomputed render state (preferred on live map). */
export const buildTrackingRouteFromRenderState = ({
  rideDto,
  renderState,
}: {
  rideDto: IRideDto;
  renderState: ITrackingRenderState;
}): IRideRoute | undefined => {
  if (!renderState.pointCount) {
    return undefined;
  }

  const rideRoute = getRideRouteInfo(rideDto);
  const carTrackingInfo = getRideCarInfo({
    rideDto,
    trackingDtos: undefined,
  });

  carTrackingInfo.trackingLastUpdate =
    renderState.carTrackingInfo.trackingLastUpdate;

  const trackingRoute: IRideRoute = {
    rideId: rideDto.id,
    rideStatus: rideRoute.rideStatus,
    direction: rideDto.direction,
    courseId: rideRoute.courseId,
    courseLineColor: rideRoute.courseLineColor,
    carTrackingInfo,
    isTrackingRoute: true,
    trackingRenderState: renderState,
  };
  return trackingRoute;
};

/** Builds a live tracking polyline route for one ride from socket/API tracking DTOs. */
export const buildTrackingRouteForRide = ({
  rideDto,
  trackingDtos,
}: {
  rideDto: IRideDto;
  trackingDtos: ITrackingInfoDto[] | undefined;
}): IRideRoute | undefined => {
  const trackingLocations = trackingDtosToLocations(trackingDtos ?? []);

  if (!trackingLocations.length) {
    return undefined;
  }

  const rideRoute = getRideRouteInfo(rideDto);
  const carTrackingInfo = getRideCarInfo({
    rideDto,
    trackingDtos,
  });

  const trackingRenderState = buildTrackingRenderStateFromDtos({
    rideId: rideDto.id ?? 0,
    trackingDtos: trackingDtos ?? [],
  });

  const trackingRoute: IRideRoute = {
    rideId: rideDto.id,
    rideStatus: rideRoute.rideStatus,
    direction: rideDto.direction,
    courseId: rideRoute.courseId,
    courseLineColor: rideRoute.courseLineColor,
    locations: trackingLocations,
    carTrackingInfo,
    isTrackingRoute: true,
    trackingRenderState,
  };
  return trackingRoute;
};
